import React, { useCallback, useMemo, useEffect } from 'react';
import Localize from 'react-intl-universal';

import isHotkey from 'is-hotkey';
import { get } from 'lodash-es';
import { Editor, Transforms, createEditor, Element as SlateElement, Node } from 'slate';
import { Editable, withReact, useSlate, Slate } from 'slate-react';

import { useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Toolbar from '@mui/material/Toolbar';

import { isJsonString } from '@common/Util';

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code'
};
const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  );
  const isList = LIST_TYPES.includes(format);
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true
  });
  let newProperties;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format
    };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format
    };
  }
  Transforms.setNodes(editor, newProperties);
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format
    })
  );
  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );

    case 'link':
      return (
        <a
          href={element.url.startsWith('http') ? element.url : `https://${element.url}`}
          target='_blank'
          rel='noopener noreferrer'
          style={{ ...style, textDecoration: 'underline', color: 'blue' }}
          {...attributes}
          onClick={(e) => {
            e.preventDefault();
            window.open(
              element.url.startsWith('http') ? element.url : `https://${element.url}`,
              '_blank'
            );
          }}
        >
          {children}
        </a>
      );

    case 'image':
      return (
        <div
          {...attributes}
          style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}
        >
          <img
            src={element.url}
            alt='Inserted content'
            style={{ maxWidth: '100%', maxHeight: '300px' }}
          />
          {children}
        </div>
      );

    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    // eslint-disable-next-line no-param-reassign
    children = <strong>{children}</strong>;
  }
  if (leaf.code) {
    // eslint-disable-next-line no-param-reassign
    children = <code>{children}</code>;
  }
  if (leaf.italic) {
    // eslint-disable-next-line no-param-reassign
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    // eslint-disable-next-line no-param-reassign
    children = <u>{children}</u>;
  }
  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({ format = null, icon = '', disabled = false }) => {
  const editor = useSlate();

  return (
    <Button
      sx={{ minWidth: 10, mr: 1 }}
      disabled={disabled}
      variant={
        isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type')
          ? 'contained'
          : 'outlined'
      }
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

const MarkButton = ({ format = null, icon = '', disabled = false }) => {
  const editor = useSlate();

  return (
    <Button
      sx={{ minWidth: 10, mr: 1 }}
      disabled={disabled}
      variant={isMarkActive(editor, format) ? 'contained' : 'outlined'}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

export function slateSerializer(nodes = []) {
  if (!Array.isArray(nodes)) {
    return null;
  }
  return nodes.map((n) => Node.string(n)).join('\n');
}

export function deserializeSlateValue(value = '') {
  if (!value || (typeof value === 'string' && !isJsonString(value))) {
    return [{ type: 'paragraph', children: [{ text: value || '' }] }];
  }

  let parsedValue = {};
  try {
    parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
  } catch (error) {
    console.warn('Failed to parse Slate value:', error);
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  if (Array.isArray(parsedValue) && parsedValue.length > 0) {
    return parsedValue;
  }

  if (parsedValue && typeof parsedValue === 'object' && 'blocks' in parsedValue) {
    return Array.isArray(parsedValue.blocks) && parsedValue.blocks.length
      ? parsedValue.blocks
      : [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  if (parsedValue && typeof parsedValue === 'object' && 'document' in parsedValue) {
    const blocks = Array.isArray(parsedValue.document?.blocks) ? parsedValue.document.blocks : [];
    return blocks.length ? blocks : [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  return [{ type: 'paragraph', children: [{ text: '' }] }];
}

export const readStyledText = (from = {}, field = '') => {
  const value = get(from, field, '');

  if (isJsonString(value)) {
    return slateSerializer(JSON.parse(value));
  }

  return slateSerializer([
    {
      type: 'paragraph',
      children: [
        {
          text: value
        }
      ]
    }
  ]);
};

const RichTextEditor = ({
  initialValue = '',
  label = '',
  disabled = false,
  isToolbarHidden = false,
  maxHeight = '',
  emptyTextMessage = null,
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  customStyles = {}
}) => {
  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(() => withReact(createEditor()), []);
  const renderValue = useMemo(() => deserializeSlateValue(initialValue), [initialValue]);
  const isMediumScreen = useMediaQuery('(min-width:900px)');

  const isEditorEmpty = (val) => {
    return val.length === 1 && val[0].children.length === 1 && val[0].children[0].text === '';
  };
  useEffect(() => {
    if (isEditorEmpty(renderValue)) {
      Transforms.select(editor, Editor.start(editor, [])); // Move cursor to start
      Transforms.insertText(editor, emptyTextMessage || Localize.get('EventDetails.noContent'));
    }
  }, []);
  return (
    <>
      <Slate
        editor={editor}
        initialValue={renderValue}
        onChange={(value) => onChange(value, editor)}
        key={initialValue}
      >
        {!isToolbarHidden && (
          <Toolbar>
            <MarkButton disabled={disabled} format='bold' icon='format_bold' />
            <MarkButton disabled={disabled} format='italic' icon='format_italic' />
            <MarkButton disabled={disabled} format='underline' icon='format_underlined' />
            <MarkButton disabled={disabled} format='code' icon='code' />

            <BlockButton disabled={disabled} format='heading-one' icon='looks_one' />
            <BlockButton disabled={disabled} format='heading-two' icon='looks_two' />
            <BlockButton disabled={disabled} format='block-quote' icon='format_quote' />
            <BlockButton disabled={disabled} format='numbered-list' icon='format_list_numbered' />
            <BlockButton disabled={disabled} format='bulleted-list' icon='format_list_bulleted' />
            <BlockButton disabled={disabled} format='left' icon='format_align_left' />
            <BlockButton disabled={disabled} format='center' icon='format_align_center' />
            <BlockButton disabled={disabled} format='right' icon='format_align_right' />
            <BlockButton disabled={disabled} format='justify' icon='format_align_justify' />
          </Toolbar>
        )}

        <Editable
          onFocus={onFocus}
          onBlur={onBlur}
          style={{
            minHeight: isMediumScreen ? '190px' : '',
            paddingLeft: '10px',
            paddingRight: '10px',
            maxHeight: isMediumScreen ? maxHeight : '',
            overflow: 'auto',
            opacity: 1,
            ...customStyles
          }}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={label}
          readOnly={disabled}
          onKeyDown={(event) => {
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                toggleMark(editor, mark);
              }
            }
          }}
        />
      </Slate>
    </>
  );
};

export default RichTextEditor;
